import { HardHatIcon } from 'lucide-react';
import { ComponentType, createElement } from 'react';
import { Link } from 'react-router-dom';
import { ROUTES } from '../../constants/routes';
import { useAppState } from '../../context/AppStateContext';
import {
  MoldOrderStatus,
  MoldOrderType,
  MoldOrderWithMold,
} from '../../types/mold';
import { getBangkokDateWithOffset } from '../../utils/dateUtils';
import { getIconComponent } from '../../utils/icon';
import {
  getFieldLabel,
  getSubtitleFieldRenderer,
} from '../MoldOrderList/fieldRenderers';

interface TaskRowProps {
  moldOrder: MoldOrderWithMold;
  isApproval: boolean;
  compact?: boolean;
}

const getPriorityPresentation = (priority?: number) => {
  if (priority != null && priority <= 1) {
    return {
      label: 'เร่งด่วนมาก',
      className: 'border-red-200 bg-red-50 text-red-700',
    };
  }
  if (priority != null && priority <= 100) {
    return {
      label: 'เร่งด่วน',
      className: 'border-orange-200 bg-orange-50 text-orange-700',
    };
  }
  return {
    label: 'ปกติ',
    className: 'border-gray-200 bg-gray-50 text-gray-600',
  };
};

const getDueDatePresentation = (rawDueDate?: string) => {
  if (!rawDueDate) {
    return {
      dateLabel: 'ไม่ระบุกำหนด',
      dateTitle: undefined,
      statusLabel: null,
      statusClassName: '',
    };
  }

  const dueDateKey = rawDueDate.slice(0, 10);
  const dueDate = new Date(`${dueDateKey}T00:00:00+07:00`);
  if (Number.isNaN(dueDate.getTime())) {
    return {
      dateLabel: 'กำหนดไม่ถูกต้อง',
      dateTitle: undefined,
      statusLabel: null,
      statusClassName: '',
    };
  }

  const todayKey = getBangkokDateWithOffset().toISOString().slice(0, 10);
  const isOverdue = dueDateKey < todayKey;

  return {
    dateLabel: `กำหนด: ${dueDate.toLocaleDateString('th-TH', {
      month: 'short',
      day: 'numeric',
      timeZone: 'Asia/Bangkok',
    })}`,
    dateTitle: `วันครบกำหนด ${dueDate.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'Asia/Bangkok',
    })}`,
    statusLabel: isOverdue ? 'เลยกำหนด' : 'ตามกำหนด',
    statusClassName: isOverdue
      ? 'border-red-200 bg-red-50 text-red-700'
      : 'border-emerald-200 bg-emerald-50 text-emerald-700',
  };
};

const TaskRow = ({ moldOrder, isApproval, compact = false }: TaskRowProps) => {
  const { toMoldOrderGroup, getTaskRowConfig, getStatusColor, getStatusText } =
    useAppState();
  const moldOrderGroup = toMoldOrderGroup(moldOrder.type as MoldOrderType);
  const IconComponent = moldOrderGroup?.uiConfig
    ? (getIconComponent(moldOrderGroup.uiConfig) ?? HardHatIcon)
    : HardHatIcon;
  const TypedIconComponent = IconComponent as ComponentType<
    Record<string, unknown>
  >;
  const taskRowConfig = getTaskRowConfig();
  const InfoFieldRenderer = getSubtitleFieldRenderer(taskRowConfig.infoField);
  const infoFieldLabel = getFieldLabel(taskRowConfig.infoField);
  const infoFieldValue =
    taskRowConfig.infoField === 'productParts'
      ? moldOrder.mold?.productParts
          ?.map((part) => part.partNumber)
          .join(', ') || ''
      : String(
          moldOrder.mold?.[
            taskRowConfig.infoField as keyof typeof moldOrder.mold
          ] || '',
        );
  const priority = getPriorityPresentation(moldOrder.priority);
  const dueDate = getDueDatePresentation(moldOrder.orderDetail?.dueDate);
  const moldId = moldOrder.mold?.sbiId || 'ไม่ระบุ';
  const statusLabel = moldOrder.status
    ? getStatusText(
        moldOrder.status as MoldOrderStatus,
        moldOrder.type as MoldOrderType,
        moldOrder.orderWorkflowDefinition,
      )
    : 'ไม่ระบุสถานะ';
  const statusClassName = moldOrder.status
    ? getStatusColor(
        moldOrder.status as MoldOrderStatus,
        moldOrder.type as MoldOrderType,
      )
    : 'bg-gray-100 text-gray-600';

  return (
    <li className="py-1.5 first:pt-0 last:pb-0">
      <Link
        to={`${ROUTES.MOLD_ORDERS}/${moldOrder.id}?source=dashboard`}
        aria-label={`เปิดใบงาน ${moldOrder.id}`}
        className={`group block rounded-xl border bg-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 lg:grid lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center lg:gap-5 lg:p-4 ${
          compact ? 'p-2.5 sm:p-3' : 'p-3 sm:p-4'
        } ${
          isApproval
            ? 'border-amber-200 hover:bg-amber-50/50'
            : 'border-gray-200 hover:border-blue-200 hover:bg-blue-50/40'
        }`}
      >
        <div className="flex min-w-0 items-start gap-3">
          <div className="relative hidden h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 ring-1 ring-blue-100 lg:flex">
            {createElement(TypedIconComponent, {
              className: 'h-5 w-5 text-blue-600',
              'aria-hidden': true,
              ...moldOrderGroup?.uiConfig?.icon.props,
            })}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
              <h3 className="min-w-0 text-sm font-bold leading-5 text-blue-700 sm:text-base">
                <span className="mr-1 font-medium text-gray-500">ประเภท:</span>
                {moldOrderGroup?.title || 'ไม่ทราบประเภท'}
              </h3>
              <span
                className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${statusClassName}`}
              >
                สถานะ: {statusLabel}
              </span>
              {isApproval && (
                <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                  รออนุมัติ
                </span>
              )}
              <span
                className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${priority.className}`}
              >
                {priority.label}
              </span>
            </div>

            <div
              className={`mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600 ${compact ? 'lg:flex' : ''}`}
            >
              <span>
                ใบงาน{' '}
                <strong className="font-semibold text-gray-900">
                  #{moldOrder.id}
                </strong>
              </span>
              <span className="min-w-0 max-w-full">
                รหัสแม่พิมพ์{' '}
                <strong
                  className="break-all font-semibold text-gray-900"
                  title={moldId}
                >
                  {moldId}
                </strong>
              </span>
            </div>

            <div
              className={`mt-2 flex-col gap-1.5 sm:flex-row sm:items-start sm:gap-2 ${compact ? 'hidden lg:flex' : 'flex'}`}
            >
              <span className="shrink-0 text-xs font-medium text-gray-500">
                {infoFieldLabel}
              </span>
              <div className="flex min-w-0 flex-wrap gap-1.5 text-xs text-gray-700 [&>span]:max-w-full [&>span]:break-all">
                {createElement(InfoFieldRenderer, {
                  moldOrder,
                  value: infoFieldValue,
                  fieldName: taskRowConfig.infoField,
                })}
              </div>
            </div>
          </div>
        </div>

        <div
          className={`flex items-center justify-between gap-3 lg:mt-0 lg:min-w-[9rem] lg:flex-col lg:items-end lg:border-l lg:border-t-0 lg:py-1 lg:pl-5 ${
            compact ? 'mt-2 lg:pl-5' : 'mt-3 border-t border-gray-100 pt-3'
          }`}
        >
          <span className="text-xs text-gray-500" title={dueDate.dateTitle}>
            {dueDate.dateLabel}
          </span>
          {dueDate.statusLabel && (
            <span
              className={`shrink-0 rounded-full border px-2.5 py-1 text-xs font-semibold ${dueDate.statusClassName}`}
            >
              {dueDate.statusLabel}
            </span>
          )}
        </div>
      </Link>
    </li>
  );
};

export default TaskRow;
